import { forwardRef } from 'react'
import { cn } from '../../lib/cn.js'
import { Input } from './Input.js'

export interface DurationInputProps {
  /** Minutes — what the API stores and what this reports. */
  value: number | null
  onValueChange: (minutes: number | null) => void
  id?: string
  /** The rule under the control. See `TagInput` for why this is here. */
  'aria-describedby'?: string
  invalid?: boolean
  disabled?: boolean
  /** Upper bound in minutes. Default 24 hours. */
  max?: number
  className?: string
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))

/**
 * How long something runs, in hours and minutes.
 *
 * Events and classes are stored as `duration_minutes`, and a single number box
 * asks somebody to do arithmetic to say "an hour and a half" — then quietly
 * accepts 15 when they meant 150. Two boxes make the units impossible to get
 * wrong and the number impossible to misread at a glance.
 *
 * It still speaks minutes to everything outside itself, so nothing downstream
 * has to know it was ever split in two.
 */
export const DurationInput = forwardRef<HTMLInputElement, DurationInputProps>(
  function DurationInput(
    { value, onValueChange, id, invalid, disabled, max = 24 * 60, className, ...aria },
    ref,
  ) {
    const total = value ?? 0
    const hours = Math.floor(total / 60)
    const minutes = total % 60

    const emit = (h: number, m: number) => {
      const next = clamp(h * 60 + m, 0, max)
      onValueChange(next === 0 && value === null ? null : next)
    }

    const parse = (s: string) => {
      const n = Number.parseInt(s, 10)
      return Number.isFinite(n) && n >= 0 ? n : 0
    }

    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Input
          ref={ref}
          id={id}
          {...aria}
          type="number"
          min={0}
          max={Math.floor(max / 60)}
          inputMode="numeric"
          value={String(hours)}
          disabled={disabled}
          invalid={invalid}
          aria-label="Hours"
          /* A format hint, not a value: the box is 80px and "Enter the hours"
             would not fit, and "hh" says the shape without looking filled in. */
          placeholder="hh"
          className="w-20"
          onChange={(e) => emit(parse(e.target.value), minutes)}
        />
        <span className="text-second text-ink-muted">h</span>
        <Input
          type="number"
          min={0}
          max={59}
          step={5}
          inputMode="numeric"
          value={String(minutes)}
          disabled={disabled}
          invalid={invalid}
          aria-label="Minutes"
          placeholder="mm"
          className="w-20"
          // Rolls over rather than rejecting: typing 90 in the minutes box means
          // an hour and a half, which is what the person meant.
          onChange={(e) => {
            const m = parse(e.target.value)
            emit(hours + Math.floor(m / 60), m % 60)
          }}
        />
        <span className="text-second text-ink-muted">min</span>
      </div>
    )
  },
)
